import type { TonyteUser } from '../types';

export function computeCompletionScore(profile: TonyteUser | null): {
  score: number;
  missing: string[];
} {
  if (!profile) return { score: 0, missing: [] };
  const missing: string[] = [];
  let earned = 0;

  const add = (pts: number, label: string, condition: boolean) => {
    if (condition) { earned += pts; } else { missing.push(label); }
  };

  const hasProfilePhoto = Boolean(
    profile.profilePhotoPath ||
    profile.profileImagePath ||
    profile.photoPath ||
    profile.profilePhotoURL ||
    profile.profileImage ||
    (profile.photo && !profile.photo.includes('randomuser')),
  );
  const hasCoverPhoto = Boolean(
    profile.coverPhotoPath ||
    profile.coverPhotoURL ||
    (profile.coverPhoto && !profile.coverPhoto.includes('unsplash')),
  );
  const hasLocation = Boolean(
    profile.locationCity?.trim() ||
    profile.city?.trim() ||
    profile.locationLabel?.trim(),
  );

  add(8,  'Display name',     Boolean(profile.name?.trim()));
  add(8,  'Date of birth',    Boolean(profile.dateOfBirth || profile.ageGroup || profile.age));
  add(14, 'Profile photo',    hasProfilePhoto);
  add(10, 'Cover photo',      hasCoverPhoto);
  add(12, 'Bio',              Boolean(profile.bio?.trim()));
  add(10, 'Social vibe',      Boolean(profile.socialVibe));
  add(12, 'Interests',        (profile.interests?.length ?? 0) >= 3);
  add(10, 'Location',         hasLocation);
  add(6,  'Music taste',      (profile.musicTaste?.length ?? 0) > 0);
  add(5,  'Languages',        (profile.languages?.length ?? 0) > 0);
  add(5,  'Going-out style',  (profile.goingOutStyle?.length ?? 0) > 0);

  return { score: Math.round(earned), missing };
}

export function buildCompletionPatch(score: number, missing: string[]): Record<string, unknown> {
  return {
    profileCompletionPercent: score,
    nextProfileStep: missing[0] ?? '',
    missingProfileFields: missing,
  };
}

export type ProfileCompletionField =
  | 'profile_photo'
  | 'date_of_birth'
  | 'interests'
  | 'location'
  | 'bio'
  | 'cover'
  | 'social_vibe'
  | 'music'
  | 'languages'
  | 'going_out';

const LABEL_TO_FIELD: Record<string, ProfileCompletionField> = {
  'Profile photo': 'profile_photo',
  'Date of birth': 'date_of_birth',
  'Interests': 'interests',
  'Location': 'location',
  'Bio': 'bio',
  'Cover photo': 'cover',
  'Social vibe': 'social_vibe',
  'Music taste': 'music',
  'Languages': 'languages',
  'Going-out style': 'going_out',
};

export function getMissingProfileCompletionFields(profile: TonyteUser | null): ProfileCompletionField[] {
  const { missing } = computeCompletionScore(profile);
  return missing
    .map(label => LABEL_TO_FIELD[label])
    .filter((step): step is ProfileCompletionField => step !== undefined);
}
